import React, { useState, useEffect } from "react";
import { Card, Row, Col, Accordion, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { ROLES, ROLE_LABELS } from "../config/permissions";
import { API_BASE_URL } from "../constants/api";

export default function Aide() {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [activeTopic, setActiveTopic] = useState("Tous");
    const [searchQuery, setSearchQuery] = useState("");
    const [faqData, setFaqData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUser();
        fetchFaqs();
    }, []);

    const loadUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                const userData = JSON.parse(userStr);
                setUser(userData);
                setUserRole(userData.role || 'commercial');
            }
        } catch (error) {
            console.error("Erreur chargement utilisateur:", error);
        }
    };

    const fetchFaqs = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/aide.php?action=faqs`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setFaqData(result.data);
                }
            }
        } catch (error) {
            console.error("Erreur chargement FAQs:", error);
        } finally {
            setLoading(false);
        }
    };

    // FAQs de base (accessibles à tous)
    const baseFaqs = [
        {
            question: "Comment scanner un code-barres ?",
            answer: "Utilisez le module Scanner dans Stock > Scanner codes-barres. Vous pouvez utiliser la caméra de votre appareil pour scanner directement, ou saisir le code manuellement. Le produit sera automatiquement recherché dans votre base.",
            category: "scanner",
            roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL, ROLES.MAGASINIER]
        },
        {
            question: "Comment créer une facture ?",
            answer: "Allez dans Ventes > Factures > Nouvelle facture. Remplissez les informations du client, sélectionnez les produits, appliquez les éventuelles remises et validez. La facture sera automatiquement numérotée et pourra être envoyée par email au client.",
            category: "factures",
            roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
        },
        {
            question: "Comment gérer les stocks ?",
            answer: "Le module Stock vous permet de gérer les entrées/sorties, faire des inventaires et suivre les mouvements. Vous pouvez également configurer des alertes pour être notifié quand un produit atteint son seuil critique.",
            category: "stock",
            roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.MAGASINIER]
        }
    ];

    // FAQs additionnelles pour les rôles spécifiques
    const roleSpecificFaqs = {
        [ROLES.ADMIN]: [
            {
                question: "Comment gérer les utilisateurs ?",
                answer: "Allez dans Paramètres > Utilisateurs. Vous pouvez ajouter, modifier ou désactiver des utilisateurs. Chaque utilisateur peut avoir un rôle différent (Admin, Gestionnaire, Commercial, Magasinier) avec des permissions spécifiques.",
                category: "utilisateurs",
                roles: [ROLES.ADMIN]
            },
            {
                question: "Comment configurer les taxes ?",
                answer: "Dans Paramètres > Société > Taxes, vous pouvez configurer les taux de TVA et CSS. Ces taux seront appliqués automatiquement sur vos factures et devis.",
                category: "parametres",
                roles: [ROLES.ADMIN]
            },
            {
                question: "Comment gérer la numérotation des documents ?",
                answer: "Dans Paramètres > Documents > Numérotation, vous pouvez personnaliser les préfixes et le format des numéros de factures, devis, commandes et bons de livraison.",
                category: "parametres",
                roles: [ROLES.ADMIN]
            }
        ],
        [ROLES.GESTIONNAIRE]: [
            {
                question: "Comment analyser les performances ?",
                answer: "La section Statistiques vous permet de suivre votre chiffre d'affaires, les ventes par produit, et d'exporter des rapports détaillés pour analyser vos performances.",
                category: "statistiques",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE]
            },
            {
                question: "Comment gérer les achats ?",
                answer: "Le module Achats vous permet de gérer vos commandes fournisseurs, suivre les réceptions et évaluer vos fournisseurs.",
                category: "achats",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE]
            }
        ],
        [ROLES.COMMERCIAL]: [
            {
                question: "Comment créer un devis ?",
                answer: "Dans Ventes > Devis > Nouveau devis, créez vos propositions commerciales. Vous pouvez les envoyer par email et les convertir en facture une fois acceptés.",
                category: "devis",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
            },
            {
                question: "Comment gérer les règlements clients ?",
                answer: "Dans Ventes > Règlements, vous pouvez enregistrer les paiements des clients et suivre les échéances.",
                category: "reglements",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
            }
        ],
        [ROLES.MAGASINIER]: [
            {
                question: "Comment faire un inventaire ?",
                answer: "Dans Stock > Inventaire, créez un nouvel inventaire. Scannez ou saisissez les quantités réelles pour chaque produit. L'application calculera automatiquement les écarts et mettra à jour le stock après validation.",
                category: "stock",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.MAGASINIER]
            },
            {
                question: "Comment suivre la traçabilité ?",
                answer: "Le module Traçabilité vous permet de suivre l'historique complet des mouvements de chaque produit, avec les dates, quantités et utilisateurs.",
                category: "stock",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.MAGASINIER]
            }
        ]
    };

    // Combiner toutes les FAQs accessibles selon le rôle
    const getAllFaqs = () => {
        let allFaqs = [...baseFaqs];
        
        if (userRole && roleSpecificFaqs[userRole]) {
            allFaqs = [...allFaqs, ...roleSpecificFaqs[userRole]];
        }
        
        // Ajouter quelques FAQs communes à tous
        const commonFaqs = [
            {
                question: "Comment exporter des rapports ?",
                answer: "Dans la section Statistiques, vous pouvez exporter les graphiques et données au format PDF ou Excel. Utilisez les filtres pour personnaliser vos rapports et cliquez sur le bouton d'export en haut à droite.",
                category: "statistiques",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
            },
            {
                question: "Comment réinitialiser mon mot de passe ?",
                answer: "Sur la page de connexion, cliquez sur 'Mot de passe oublié' et suivez les instructions. Un email vous sera envoyé pour réinitialiser votre mot de passe.",
                category: "securite",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL, ROLES.MAGASINIER]
            }
        ];
        
        allFaqs = [...allFaqs, ...commonFaqs];
        
        // Supprimer les doublons éventuels
        return allFaqs.filter((faq, index, self) => 
            index === self.findIndex(f => f.question === faq.question)
        );
    };

    // Filtrer les guides selon le rôle
    const getGuidesByRole = () => {
        const allGuides = [
            {
                title: "Démarrage rapide",
                description: "Configurez votre entreprise en 5 minutes",
                icon: "ri-rocket-line",
                color: "#21ACCD",
                link: "/parametres/societe/infos",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE]
            },
            {
                title: "Gestion des ventes",
                description: "Factures, devis, clients et règlements",
                icon: "ri-shopping-bag-3-line",
                color: "#f59e0b",
                link: "/ventes/factures/liste",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
            },
            {
                title: "Gestion des achats",
                description: "Fournisseurs, commandes et réceptions",
                icon: "ri-shopping-cart-line",
                color: "#10b981",
                link: "/achats/commandes",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE]
            },
            {
                title: "Gestion du stock",
                description: "Produits, inventaires et mouvements",
                icon: "ri-stack-line",
                color: "#8b5cf6",
                link: "/stock/etat/instantane",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.MAGASINIER]
            },
            {
                title: "Scanner codes-barres",
                description: "Utilisez la caméra pour scanner",
                icon: "ri-qr-code-line",
                color: "#ec4899",
                link: "/stock/scanner",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL, ROLES.MAGASINIER]
            },
            {
                title: "Statistiques",
                description: "Analysez vos performances",
                icon: "ri-bar-chart-2-line",
                color: "#f97316",
                link: "/stats/dashboard",
                roles: [ROLES.ADMIN, ROLES.GESTIONNAIRE, ROLES.COMMERCIAL]
            },
            {
                title: "Paramètres",
                description: "Configuration de l'application",
                icon: "ri-settings-3-line",
                color: "#6b7280",
                link: "/parametres/societe/taxes",
                roles: [ROLES.ADMIN]
            },
            {
                title: "Gestion des utilisateurs",
                description: "Gérez les comptes et permissions",
                icon: "ri-user-settings-line",
                color: "#f43f5e",
                link: "/parametres/utilisateurs",
                roles: [ROLES.ADMIN]
            }
        ];

        return allGuides.filter(guide => 
            guide.roles.includes(userRole) || userRole === ROLES.ADMIN
        );
    };

    const filteredFaqs = getAllFaqs().filter(faq => 
        (activeTopic === "Tous" || faq.category === activeTopic.toLowerCase()) &&
        (searchQuery === "" || faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const topics = [
        "Tous", "Factures", "Stock", "Scanner", "Statistiques", "Achats", "Utilisateurs", "Paramètres", "Sécurité"
    ];

    const getRoleColor = (role) => {
        const colors = {
            [ROLES.ADMIN]: '#dc2626',
            [ROLES.GESTIONNAIRE]: '#f59e0b',
            [ROLES.COMMERCIAL]: '#3b82f6',
            [ROLES.MAGASINIER]: '#10b981'
        };
        return colors[role] || '#6b7280';
    };

    const getRoleBadgeStyle = (role) => {
        const styles = {
            [ROLES.ADMIN]: { background: '#dc2626', label: 'Admin' },
            [ROLES.GESTIONNAIRE]: { background: '#f59e0b', label: 'Gestionnaire' },
            [ROLES.COMMERCIAL]: { background: '#3b82f6', label: 'Commercial' },
            [ROLES.MAGASINIER]: { background: '#10b981', label: 'Magasinier' }
        };
        return styles[role] || { background: '#6b7280', label: role };
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête modernisé avec dégradé */}
                    <div className="aide-header animate-fadeInUp">
                        <div className="aide-header-content">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="p-2 bg-white bg-opacity-20 rounded-3">
                                            <i className="ri-question-line fs-1 text-white"></i>
                                        </div>
                                        <div>
                                            <h1 className="display-4 fw-bold text-white mb-0">Centre d'aide</h1>
                                            {userRole && (
                                                <span className="role-badge-header" style={{ background: getRoleColor(userRole) }}>
                                                    {ROLE_LABELS[userRole] || userRole}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="lead mb-4 text-white opacity-90">
                                        {userRole === ROLES.ADMIN 
                                            ? "Administrateur - Accès complet à toute la documentation"
                                            : userRole === ROLES.GESTIONNAIRE
                                            ? "Gestionnaire - Documentation adaptée à vos responsabilités"
                                            : userRole === ROLES.COMMERCIAL
                                            ? "Commercial - Guides pour vos activités de vente"
                                            : "Magasinier - Documentation pour la gestion du stock"
                                        }
                                    </p>
                                    
                                    {/* Barre de recherche modernisée */}
                                    <div className="aide-search">
                                        <i className="ri-search-line search-icon"></i>
                                        <input 
                                            type="text" 
                                            placeholder="Rechercher une question, un guide..." 
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {searchQuery && (
                                            <button className="clear-btn" onClick={() => setSearchQuery("")}>
                                                <i className="ri-close-line"></i>
                                            </button>
                                        )}
                                        <button className="search-btn">
                                            Rechercher
                                        </button>
                                    </div>
                                </Col>
                                <Col lg={4} className="text-center d-none d-lg-block">
                                    <div className="float-animation">
                                        <i className="ri-customer-service-2-line" style={{ fontSize: '120px', opacity: 0.15, color: 'white' }}></i>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Statistiques rapides modernisées */}
                    <Row className="g-4 mb-5">
                        <Col sm={6} md={3}>
                            <div className="stats-card">
                                <div className="stats-icon" style={{ background: '#21ACCD20', color: '#21ACCD' }}>
                                    <i className="ri-time-line"></i>
                                </div>
                                <div className="stats-number">24/7</div>
                                <div className="stats-label">Support disponible</div>
                            </div>
                        </Col>
                        <Col sm={6} md={3}>
                            <div className="stats-card">
                                <div className="stats-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
                                    <i className="ri-file-list-line"></i>
                                </div>
                                <div className="stats-number">{getAllFaqs().length}+</div>
                                <div className="stats-label">Articles d'aide</div>
                            </div>
                        </Col>
                        <Col sm={6} md={3}>
                            <div className="stats-card">
                                <div className="stats-icon" style={{ background: '#10b98120', color: '#10b981' }}>
                                    <i className="ri-phone-line"></i>
                                </div>
                                <div className="stats-number">15min</div>
                                <div className="stats-label">Temps de réponse</div>
                            </div>
                        </Col>
                        <Col sm={6} md={3}>
                            <div className="stats-card">
                                <div className="stats-icon" style={{ background: '#8b5cf620', color: '#8b5cf6' }}>
                                    <i className="ri-heart-line"></i>
                                </div>
                                <div className="stats-number">98%</div>
                                <div className="stats-label">Satisfaction</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Guides recommandés modernisés */}
                    <h2 className="fw-bold mb-4" style={{ color: '#1e293b' }}>
                        <i className="ri-guide-line me-2" style={{ color: '#21ACCD' }}></i>
                        Guides recommandés pour vous
                    </h2>
                    <Row className="g-4 mb-5">
                        {getGuidesByRole().map((guide, index) => (
                            <Col lg={4} md={6} key={index}>
                                <Link to={guide.link} style={{ textDecoration: 'none' }}>
                                    <Card className="guide-card">
                                        <Card.Body className="p-4">
                                            <div className="d-flex align-items-start gap-3">
                                                <div className="guide-icon" style={{ background: `${guide.color}15`, color: guide.color }}>
                                                    <i className={guide.icon}></i>
                                                </div>
                                                <div>
                                                    <h5 className="fw-semibold mb-1" style={{ color: '#1e293b' }}>
                                                        {guide.title}
                                                    </h5>
                                                    <p className="text-muted small mb-0">{guide.description}</p>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Link>
                            </Col>
                        ))}
                    </Row>

                    <Row>
                        {/* Colonne principale - FAQs modernisées */}
                        <Col lg={8}>
                            <Card className="faq-card mb-4">
                                <Card.Header className="faq-card-header">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="faq-header-icon">
                                            <i className="ri-questionnaire-line"></i>
                                        </div>
                                        <div>
                                            <h4 className="fw-bold mb-1 text-white">Questions fréquentes</h4>
                                            <p className="mb-0 text-white opacity-75">
                                                {getAllFaqs().length} articles disponibles pour votre rôle
                                            </p>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {/* Filtres par catégorie modernisés */}
                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                        {topics.map((topic, index) => (
                                            <span
                                                key={index}
                                                className={`topic-badge ${activeTopic === topic ? 'active' : ''}`}
                                                onClick={() => setActiveTopic(topic)}
                                            >
                                                {topic}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Liste des FAQs modernisée */}
                                    {filteredFaqs.length > 0 ? (
                                        <Accordion defaultActiveKey="0" flush className="faq-accordion">
                                            {filteredFaqs.map((faq, index) => (
                                                <Accordion.Item eventKey={index} key={index} className="faq-item">
                                                    <Accordion.Header>
                                                        <span>{faq.question}</span>
                                                        {faq.roles && faq.roles.length > 0 && (
                                                            <span className="ms-2">
                                                                {faq.roles.map(role => {
                                                                    const badgeStyle = getRoleBadgeStyle(role);
                                                                    return (
                                                                        <span 
                                                                            key={role}
                                                                            className="role-mini-badge"
                                                                            style={{ background: badgeStyle.background }}
                                                                        >
                                                                            {badgeStyle.label}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </span>
                                                        )}
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <p className="faq-answer">{faq.answer}</p>
                                                        <div className="mt-3">
                                                            <span className="category-badge">
                                                                <i className="ri-price-tag-3-line me-1"></i>
                                                                {faq.category}
                                                            </span>
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="ri-questionnaire-line fs-1 text-muted mb-3"></i>
                                            <h5 className="text-muted">Aucune question trouvée</h5>
                                            <p className="text-muted small">Essayez avec d'autres mots-clés</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Colonne latérale modernisée */}
                        <Col lg={4}>
                            {/* Contact support modernisé */}
                            <Card className="contact-card mb-4">
                                <Card.Body className="p-4 text-center">
                                    <div className="contact-icon">
                                        <i className="ri-customer-service-2-line"></i>
                                    </div>
                                    <h5 className="fw-bold mb-2">Besoin d'aide ?</h5>
                                    <p className="text-muted small mb-4">
                                        Notre équipe de support est disponible 24/7 pour vous aider.
                                    </p>
                                    <div className="d-grid gap-3">
                                        <Button 
                                            variant="primary" 
                                            className="w-100 py-2 rounded-pill"
                                            style={{ background: '#21ACCD', border: 'none' }}
                                        >
                                            <i className="ri-mail-line me-2"></i>
                                            Envoyer un email
                                        </Button>
                                        <Button 
                                            variant="outline-primary" 
                                            className="w-100 py-2 rounded-pill"
                                            style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                        >
                                            <i className="ri-phone-line me-2"></i>
                                            Support téléphonique
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Documentation utile */}
                            <Card className="doc-card">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                                        <i className="ri-file-text-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Documentation utile
                                    </h6>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-3">
                                            <Link to="#" className="doc-link">
                                                <i className="ri-video-line me-2"></i>
                                                Tutoriels vidéo
                                            </Link>
                                        </li>
                                        <li className="mb-3">
                                            <Link to="#" className="doc-link">
                                                <i className="ri-book-open-line me-2"></i>
                                                Guide d'utilisation
                                            </Link>
                                        </li>
                                        <li className="mb-3">
                                            <Link to="#" className="doc-link">
                                                <i className="ri-download-line me-2"></i>
                                                Télécharger la documentation
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" className="doc-link">
                                                <i className="ri-github-line me-2"></i>
                                                API Documentation
                                            </Link>
                                        </li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                /* Animations */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }

                .animate-fadeInUp {
                    animation: fadeInUp 0.6s ease-out forwards;
                }

                .float-animation {
                    animation: float 3s ease-in-out infinite;
                }

                /* Header modernisé */
                .aide-header {
                    background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
                    border-radius: 20px;
                    padding: 2rem 3rem;
                    margin-bottom: 2rem;
                    position: relative;
                    overflow: hidden;
                }

                .aide-header::before {
                    content: '';
                    position: absolute;
                    top: -50px;
                    right: -50px;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                }

                .aide-header::after {
                    content: '';
                    position: absolute;
                    bottom: -50px;
                    left: -50px;
                    width: 250px;
                    height: 250px;
                    background: rgba(255, 255, 255, 0.08);
                    border-radius: 50%;
                }

                .aide-header-content {
                    position: relative;
                    z-index: 1;
                }

                .role-badge-header {
                    display: inline-block;
                    padding: 0.25rem 1rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: white;
                    margin-left: 0.75rem;
                    vertical-align: middle;
                }

                /* Barre de recherche modernisée */
                .aide-search {
                    background: rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    border-radius: 60px;
                    padding: 0.25rem;
                    display: flex;
                    align-items: center;
                    max-width: 500px;
                    gap: 0.5rem;
                }

                .aide-search .search-icon {
                    color: white;
                    font-size: 1.2rem;
                    margin-left: 1rem;
                }

                .aide-search input {
                    flex: 1;
                    background: transparent;
                    border: none;
                    padding: 0.75rem 0;
                    color: white;
                    font-size: 0.9rem;
                }

                .aide-search input::placeholder {
                    color: rgba(255, 255, 255, 0.7);
                }

                .aide-search input:focus {
                    outline: none;
                }

                .aide-search .clear-btn {
                    background: transparent;
                    border: none;
                    color: rgba(255, 255, 255, 0.7);
                    cursor: pointer;
                    padding: 0;
                }

                .aide-search .search-btn {
                    background: white;
                    border: none;
                    border-radius: 50px;
                    padding: 0.5rem 1.5rem;
                    color: #21ACCD;
                    font-weight: 600;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                }

                .aide-search .search-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                /* Statistiques Cards */
                .stats-card {
                    background: white;
                    border-radius: 16px;
                    padding: 1.25rem;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                }

                .stats-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
                }

                .stats-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    margin: 0 auto 0.75rem;
                }

                .stats-number {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1e293b;
                }

                .stats-label {
                    font-size: 0.75rem;
                    color: #64748b;
                }

                /* Guide Cards */
                .guide-card {
                    border: none;
                    border-radius: 16px;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    height: 100%;
                }

                .guide-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(33, 172, 205, 0.15);
                }

                .guide-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    flex-shrink: 0;
                }

                /* FAQ Cards */
                .faq-card {
                    border: none;
                    border-radius: 16px;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                }

                .faq-card-header {
                    background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
                    padding: 1.25rem 1.5rem;
                    border: none;
                }

                .faq-header-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: rgba(255, 255, 255, 0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    color: white;
                }

                /* Topic Badges */
                .topic-badge {
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0.4rem 1rem;
                    border-radius: 50px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .topic-badge:hover {
                    background: #e2e8f0;
                    transform: translateY(-1px);
                }

                .topic-badge.active {
                    background: #21ACCD;
                    color: white;
                }

                /* FAQ Accordion */
                .faq-accordion .faq-item {
                    border: none;
                    border-bottom: 1px solid #e2e8f0;
                    background: transparent;
                }

                .faq-accordion .faq-item:last-child {
                    border-bottom: none;
                }

                .faq-accordion .accordion-button {
                    padding: 1rem 0;
                    font-weight: 600;
                    color: #1e293b;
                    background: transparent;
                    box-shadow: none;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }

                .faq-accordion .accordion-button:not(.collapsed) {
                    color: #21ACCD;
                    background: transparent;
                }

                .faq-accordion .accordion-button::after {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%2321ACCD'%3E%3Cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E");
                }

                .faq-accordion .accordion-body {
                    padding: 0 0 1rem 0;
                }

                .faq-answer {
                    color: #64748b;
                    line-height: 1.6;
                    font-size: 0.9rem;
                }

                .role-mini-badge {
                    display: inline-block;
                    padding: 0.2rem 0.5rem;
                    border-radius: 50px;
                    font-size: 0.65rem;
                    font-weight: 600;
                    color: white;
                    margin-left: 0.25rem;
                }

                .category-badge {
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0.25rem 0.75rem;
                    border-radius: 50px;
                    font-size: 0.7rem;
                }

                /* Contact Card */
                .contact-card {
                    border: none;
                    border-radius: 16px;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .contact-icon {
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.8rem;
                    color: white;
                    margin: 0 auto 1rem;
                }

                /* Documentation Card */
                .doc-card {
                    border: none;
                    border-radius: 16px;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .doc-link {
                    color: #64748b;
                    text-decoration: none;
                    font-size: 0.85rem;
                    transition: all 0.3s ease;
                    display: inline-block;
                }

                .doc-link:hover {
                    color: #21ACCD;
                    transform: translateX(4px);
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .aide-header {
                        padding: 1.5rem;
                    }
                    
                    .aide-search .search-btn {
                        display: none;
                    }
                    
                    .stats-number {
                        font-size: 1.25rem;
                    }
                }
            `}</style>
        </>
    );
}